 <script setup lang="ts">

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { ArrowLeft, Loader2 } from 'lucide-vue-next'
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import LoginCard from '@/views/auth/components/LoginCard.vue'
import { Button } from '@/components/ui/button'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { useForm } from 'vee-validate'
import { defineEmits, ref } from 'vue'
import { AuthApi } from '@/services/api/auth.ts'
import { AuthService } from '@/services/auth.ts'

const emit = defineEmits<{
  (event: 'back'): void
}>()

const formSchema = toTypedSchema(
  z.object({
    email: z.string().email('Wrong email format').min(1, 'Email is required'),
  }),
)

const form = useForm({
  validationSchema: formSchema,
})

const isLoading = ref<boolean>(false)
const errorMessage = ref<string>('')

const onSubmit = form.handleSubmit(async (values) => {
  try {
    isLoading.value = true
    const response = await AuthApi.entraRedirect(values)
    AuthService.shouldRemember(true)
    AuthService.setOktaKey(response.internal)
    window.location.href = response.url;
  } catch (error) {
    errorMessage.value = error.response.data.description
  }
  isLoading.value = false
})

</script>

<template>
  <LoginCard>
    <form @submit="onSubmit">
      <CardHeader class="gap-0">
        <img
          src="/src/assets/media/brand-logos/microsoft-entra.svg"
          class="shrink-0 h-12 text-center p-0 m-0"
          alt="Revio"
        />
        <CardTitle class="text-center text-base mb-3 mt-3">Microsoft Entra ID</CardTitle>
        <CardDescription class="text-center">Enter your email address to redirect to your organization's Entra domain. If your organization has not yet integrated Entra with Revio, contact our team.

        </CardDescription>
      </CardHeader>

      <div
        v-if="errorMessage"
        class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg"
      >
        {{ errorMessage }}
      </div>

      <FormField v-slot="{ componentField }" name="email">
        <FormItem class="mb-4">
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input id="email" type="email" v-bind="componentField" placeholder="Enter email" @keydown.enter="onSubmit"/>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <Button class="mt-4 w-full" type="submit" :disabled="isLoading">
        <Loader2 v-if="isLoading" class="size-4 mr-2 animate-spin" />
        Sign In
      </Button>
      <Button variant="ghost" class="mt-4 w-full text-gray-600" @click.prevent="emit('back')">
        <ArrowLeft />
        Back to Login
      </Button>
    </form>
  </LoginCard>
</template>

<style scoped>

</style>
